<script lang="ts">
    import { onDestroy } from "svelte";
    import { get } from "svelte/store";
    import { TilemapEditsManager } from "@workadventure/map-editor";
    import { LL } from "../../../../i18n/i18n-svelte";
    import { gameManager } from "../../../Phaser/Game/GameManager";
    import {
        mapEditorSelectedTileLayerIdStore,
        mapEditorTileLayersRevisionStore,
    } from "../../../Stores/MapEditorStore";
    import { CreateTileLayerFrontCommand } from "../../../Phaser/Game/MapEditor/Commands/Tilemap/CreateTileLayerFrontCommand";
    import { DeleteTileLayerFrontCommand } from "../../../Phaser/Game/MapEditor/Commands/Tilemap/DeleteTileLayerFrontCommand";
    import { MoveTileLayerFrontCommand } from "../../../Phaser/Game/MapEditor/Commands/Tilemap/MoveTileLayerFrontCommand";
    import { UpdateTileLayerFrontCommand } from "../../../Phaser/Game/MapEditor/Commands/Tilemap/UpdateTileLayerFrontCommand";
    import type { FrontCommand } from "../../../Phaser/Game/MapEditor/Commands/FrontCommand";
    import { IconEye, IconGhost, IconTrash, IconPencil } from "@wa-icons";

    const gameScene = gameManager.getCurrentGameScene();
    const gameMapFrontWrapper = gameScene.getGameMapFrontWrapper();

    type LayerViewModel = {
        id: number;
        name: string;
        visible: boolean;
        opacity: number;
    };

    function buildLayerViewModels(): LayerViewModel[] {
        const viewModels: LayerViewModel[] = [];
        for (const layer of gameMapFrontWrapper.getFlatLayers()) {
            if (layer.type !== "tilelayer") {
                continue;
            }
            viewModels.push({
                id: layer.id,
                name: layer.name,
                visible: layer.visible ?? true,
                opacity: layer.opacity ?? 1,
            });
        }
        return viewModels;
    }

    function defaultLayerId(): number | undefined {
        const specialNames = new Set(["start", "collisions"]);
        const firstUsable = gameMapFrontWrapper
            .getFlatLayers()
            .find((layer) => layer.type === "tilelayer" && !specialNames.has(layer.name));
        const first = gameMapFrontWrapper.getFlatLayers().find((layer) => layer.type === "tilelayer");
        const fallback = firstUsable ?? first;
        return fallback && fallback.type === "tilelayer" ? fallback.id : undefined;
    }

    let layers = $state<LayerViewModel[]>(buildLayerViewModels());
    let selectedLayerId = $state<number | undefined>(undefined);
    let ghostedLayerId = $state<number | undefined>(undefined);
    /** Layer currently being renamed (inline input). */
    let renamingLayerId = $state<number | undefined>(undefined);
    let renamingValue = $state("");

    $effect(() => {
        const current = get(mapEditorSelectedTileLayerIdStore);
        if (current !== undefined && layers.some((layer) => layer.id === current)) {
            return;
        }
        const defaultId = defaultLayerId();
        if (defaultId !== undefined) {
            mapEditorSelectedTileLayerIdStore.set(defaultId);
        }
    });

    // Rebuild the list whenever a layer is created/deleted/moved/renamed (locally or remotely).
    $effect(() => {
        const revisionUnsubscriber = mapEditorTileLayersRevisionStore.subscribe(() => {
            layers = buildLayerViewModels();
        });
        return revisionUnsubscriber;
    });

    const selectedLayerUnsubscriber = mapEditorSelectedTileLayerIdStore.subscribe((id) => {
        selectedLayerId = id;
    });

    onDestroy(() => {
        selectedLayerUnsubscriber();
        if (ghostedLayerId !== undefined) {
            gameMapFrontWrapper.setLayerGhostById(ghostedLayerId, false);
        }
    });

    const selectedLayer = $derived(layers.find((layer) => layer.id === selectedLayerId));

    function selectLayer(layerId: number) {
        mapEditorSelectedTileLayerIdStore.set(layerId);
    }

    function executeLayerCommand(command: FrontCommand) {
        gameScene
            .getMapEditorModeManager()
            .executeCommand(command)
            .catch((e) => console.error(e));
    }

    function createLayer() {
        const wamFile = gameScene.getGameMap().getWamFile();
        if (!wamFile) {
            return;
        }
        const layerId = TilemapEditsManager.generateLayerId(gameMapFrontWrapper.getMap());
        const name = $LL.mapEditor.tilemapEditor.defaultLayerName({ index: layers.length + 1 });
        executeLayerCommand(
            new CreateTileLayerFrontCommand(
                wamFile,
                layerId,
                name,
                layers.length,
                1,
                true,
                undefined,
                gameMapFrontWrapper,
            ),
        );
        selectLayer(layerId);
    }

    function deleteLayer(layer: LayerViewModel) {
        const wamFile = gameScene.getGameMap().getWamFile();
        if (!wamFile) {
            return;
        }
        if (ghostedLayerId === layer.id) {
            ghostedLayerId = undefined;
        }
        executeLayerCommand(new DeleteTileLayerFrontCommand(wamFile, layer.id, undefined, gameMapFrontWrapper));
    }

    function startRenaming(layer: LayerViewModel) {
        renamingLayerId = layer.id;
        renamingValue = layer.name;
    }

    function commitRenaming() {
        const layerId = renamingLayerId;
        const wamFile = gameScene.getGameMap().getWamFile();
        const name = renamingValue.trim();
        renamingLayerId = undefined;
        if (!wamFile || layerId === undefined || name === "") {
            return;
        }
        const layer = layers.find((entry) => entry.id === layerId);
        if (!layer || layer.name === name) {
            return;
        }
        executeLayerCommand(
            new UpdateTileLayerFrontCommand(wamFile, layerId, { name }, undefined, gameMapFrontWrapper),
        );
    }

    function moveLayer(layer: LayerViewModel, offset: -1 | 1) {
        const wamFile = gameScene.getGameMap().getWamFile();
        if (!wamFile) {
            return;
        }
        const index = layers.findIndex((entry) => entry.id === layer.id);
        const newIndex = index + offset;
        if (index === -1 || newIndex < 0 || newIndex >= layers.length) {
            return;
        }
        executeLayerCommand(new MoveTileLayerFrontCommand(wamFile, layer.id, newIndex, undefined, gameMapFrontWrapper));
    }

    function toggleVisibility(layer: LayerViewModel) {
        const visible = !layer.visible;
        gameMapFrontWrapper.setLayerVisibilityById(layer.id, visible);
        layers = buildLayerViewModels();
    }

    function toggleGhost(layer: LayerViewModel) {
        if (ghostedLayerId === layer.id) {
            gameMapFrontWrapper.setLayerGhostById(layer.id, false);
            ghostedLayerId = undefined;
        } else {
            if (ghostedLayerId !== undefined) {
                gameMapFrontWrapper.setLayerGhostById(ghostedLayerId, false);
            }
            gameMapFrontWrapper.setLayerGhostById(layer.id, true);
            ghostedLayerId = layer.id;
        }
    }

    function changeOpacity(layer: LayerViewModel, opacityPercent: number) {
        gameMapFrontWrapper.setLayerOpacityById(layer.id, opacityPercent / 100);
        layers = buildLayerViewModels();
    }

    function opacityToPercent(layer: LayerViewModel): number {
        return Math.round((layer.opacity ?? 1) * 100);
    }
</script>

<div class="flex flex-col gap-2">
    <div class="flex items-center justify-between">
        <h4 class="text-sm font-semibold">{$LL.mapEditor.tilemapEditor.layers()}</h4>
        <button
            type="button"
            class="p-1 rounded bg-white/10 hover:bg-white/20 text-sm leading-none"
            aria-label={$LL.mapEditor.tilemapEditor.createLayer()}
            title={$LL.mapEditor.tilemapEditor.createLayer()}
            onclick={createLayer}
        >
            +
        </button>
    </div>
    <ul class="flex flex-col gap-1 max-h-56 overflow-y-auto">
        {#each layers as layer, index (layer.id)}
            <li>
                <div
                    class="flex items-center gap-2 rounded px-2 py-1 cursor-pointer {selectedLayerId === layer.id
                        ? 'bg-secondary'
                        : 'bg-white/10 hover:bg-white/20'}"
                    onclick={() => selectLayer(layer.id)}
                    role="button"
                    tabindex="0"
                    onkeydown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                            selectLayer(layer.id);
                        }
                    }}
                >
                    {#if renamingLayerId === layer.id}
                        <input
                            type="text"
                            class="flex-1 min-w-0 text-sm bg-black/30 rounded px-1"
                            bind:value={renamingValue}
                            onclick={(event) => event.stopPropagation()}
                            onkeydown={(event) => {
                                if (event.key === "Enter") {
                                    commitRenaming();
                                } else if (event.key === "Escape") {
                                    renamingLayerId = undefined;
                                }
                            }}
                            onblur={commitRenaming}
                        />
                    {:else}
                        <!-- svelte-ignore a11y_click_events_have_key_events -->
                        <!-- svelte-ignore a11y_no_static_element_interactions -->
                        <span class="flex-1 text-sm truncate" ondblclick={() => startRenaming(layer)}>
                            {layer.name}
                        </span>
                    {/if}
                    <button
                        type="button"
                        class="p-1 rounded hover:bg-white/20"
                        aria-label={$LL.mapEditor.tilemapEditor.renameLayer()}
                        title={$LL.mapEditor.tilemapEditor.renameLayer()}
                        onclick={(event) => {
                            event.stopPropagation();
                            startRenaming(layer);
                        }}
                    >
                        <IconPencil font-size="14" />
                    </button>
                    <button
                        type="button"
                        class="p-1 rounded hover:bg-white/20 disabled:opacity-20"
                        aria-label={$LL.mapEditor.tilemapEditor.moveLayerUp()}
                        title={$LL.mapEditor.tilemapEditor.moveLayerUp()}
                        disabled={index === 0}
                        onclick={(event) => {
                            event.stopPropagation();
                            moveLayer(layer, -1);
                        }}
                    >
                        ↑
                    </button>
                    <button
                        type="button"
                        class="p-1 rounded hover:bg-white/20 disabled:opacity-20"
                        aria-label={$LL.mapEditor.tilemapEditor.moveLayerDown()}
                        title={$LL.mapEditor.tilemapEditor.moveLayerDown()}
                        disabled={index === layers.length - 1}
                        onclick={(event) => {
                            event.stopPropagation();
                            moveLayer(layer, 1);
                        }}
                    >
                        ↓
                    </button>
                    <button
                        type="button"
                        class="p-1 rounded hover:bg-white/20 {ghostedLayerId === layer.id ? 'bg-white/20' : ''}"
                        aria-label={$LL.mapEditor.tilemapEditor.ghostLayer()}
                        title={$LL.mapEditor.tilemapEditor.ghostLayer()}
                        onclick={(event) => {
                            event.stopPropagation();
                            toggleGhost(layer);
                        }}
                    >
                        <IconGhost font-size="16" class={ghostedLayerId === layer.id ? "" : "opacity-40"} />
                    </button>
                    <button
                        type="button"
                        class="p-1 rounded hover:bg-white/20"
                        aria-label={layer.visible
                            ? $LL.mapEditor.tilemapEditor.hideLayer()
                            : $LL.mapEditor.tilemapEditor.showLayer()}
                        title={layer.visible
                            ? $LL.mapEditor.tilemapEditor.hideLayer()
                            : $LL.mapEditor.tilemapEditor.showLayer()}
                        onclick={(event) => {
                            event.stopPropagation();
                            toggleVisibility(layer);
                        }}
                    >
                        <IconEye font-size="16" class={layer.visible ? "" : "opacity-30"} />
                    </button>
                    <button
                        type="button"
                        class="p-1 rounded hover:bg-white/20 text-red-400"
                        aria-label={$LL.mapEditor.tilemapEditor.deleteLayer()}
                        title={$LL.mapEditor.tilemapEditor.deleteLayer()}
                        onclick={(event) => {
                            event.stopPropagation();
                            deleteLayer(layer);
                        }}
                    >
                        <IconTrash font-size="14" />
                    </button>
                </div>
            </li>
        {/each}
    </ul>
    {#if selectedLayer}
        <div class="flex flex-col gap-1">
            <label class="text-xs flex items-center justify-between" for="tilemap-layer-opacity">
                <span>{$LL.mapEditor.tilemapEditor.opacity()}</span>
                <span>{opacityToPercent(selectedLayer)}%</span>
            </label>
            <input
                id="tilemap-layer-opacity"
                type="range"
                min="0"
                max="100"
                value={opacityToPercent(selectedLayer)}
                oninput={(event) => changeOpacity(selectedLayer, Number(event.currentTarget.value))}
            />
        </div>
    {/if}
</div>
